/**
 * Data loader — fetches the fact tables from /api/fin/dataset and transforms
 * them into the flat DataRow format the dashboard expects.
 *
 * The workbench is sliced by GROUP, not by legal entity. Five source systems
 * feed it and each one belongs to a group rather than standing on its own:
 *
 *   WWG        1C GL for WWF and WWPI (costs, revenue, balances, positions)
 *   The Vault  1C GL for Tria Bridge + the Tria Wallet feeds (transactions,
 *              balances, fees)
 *   both       fin model plan, supplier invoices, payroll — these span every
 *              entity in the registry, so they are split by group rather than
 *              belonging to one
 *
 * Group membership is `fin.entities.group_tag`, resolved per row through
 * `entity_id` where the warehouse carries one. A single company is still
 * reachable — the Entity dimension is on every shelf — but it is a slice of a
 * group view rather than a separate load.
 */

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

/**
 * A group-wide view: either "ALL", or a `fin.entities.group_tag`.
 *
 * Deliberately not a fixed union of the groups that exist today. TFF became a
 * peer of WWG and The Vault after this toggle was written, and a hardcoded
 * pair would have dropped its rows out of every view without saying so — the
 * same failure the dashboard's `group_tag !== 'wwg'` slice had. The buttons
 * are built from the groups actually present in the data, so a group added
 * later appears on its own.
 */
export type GroupView = string

/**
 * Rows whose payer resolves to no entity in the registry.
 *
 * They are real money and they are kept — visible in ALL and in the Entity
 * group dimension — but they cannot be claimed by either group view, so WWG
 * plus The Vault does not have to equal ALL. The workbench reports the
 * residual rather than letting it disappear into a filter.
 */
export const UNATTRIBUTED = '(unattributed)'

/**
 * Payers someone has looked at and ruled out — Rapid Ladder, Tokenomica,
 * LINZBURG, ilumita. Held in `fin.entity_aliases` with `is_entity = FALSE`.
 *
 * Kept apart from UNATTRIBUTED because they mean opposite things to whoever is
 * reading the residual: this one is finished work, that one is outstanding
 * work. Both stay out of the group views.
 */
export const EXTERNAL = '(external)'

/**
 * Whether the fin model's forecast reaches the analytics workbench.
 *
 * OFF, deliberately, since 16 Aug 2026. The three plan sources —
 * `budget_expense`, `budget_payroll`, `pl_plan` — share the "Cash Flow"
 * measure with booked actuals, so any chart that did not constrain data
 * source added forecast to history and presented the total as measured. A
 * warning strip was added for that in 6936c9e, but warning after the fact is
 * not the same as the numbers being right, and the plan side needs work of
 * its own before it can sit beside the actuals: 898 pl_plan rows carry no
 * entity, two of its companies exist only in the 2026 forecast, and it has no
 * scenario-aware measure to keep it apart.
 *
 * Turning this back on is this one line. Everything downstream is gated on
 * what actually loaded, so the Plan vs Actual preset, the plan templates, the
 * payroll-by-division panels and the Scenario dimension all reappear on their
 * own — none of them had to be deleted to take the forecast out.
 */
const INCLUDE_PLAN = false

export interface DataRow {
  year: string
  quarter: string
  month: string
  yearMonth: string           // "2025-01" for sorting
  entity: string
  account: string             // cost_category / revenue_group / asset_class / operation_category
  accountCode: string
  scenario: string            // "Actual" (1C) | "Plan" (fin model Excel)
  dataSource: string          // "costs" / "revenue" / "positions" / "transactions" / "balances" / "fees"
                              // | "budget_expense" / "budget_payroll" / "pl_plan" (model)
  flowType: string            // "Cash Flow" / "Assets Held" / "Transaction Volume" / "Balance"
  costType: string            // Direct / Indirect / Revenue / Position / Deposit / Withdrawal / Fee ...
  counterparty: string        // vendor or client
  counterpartyType: string    // Client / Own Account / Operational / Vendor
  clientName: string
  currency: string
  entrySide: string
  contraCategory: string
  serviceType: string          // AUM / AUC / IA / OWN (positions only)
  value: number

  /* ── Conformed dimensions ──────────────────────────────────────────────
     These come from shared reference tables rather than from each fact's own
     columns, which is what makes cross-source slicing possible: headcount
     cost from the model and actual GL cost can both be grouped by group or
     jurisdiction because both resolve through fin.entities. */
  entityGroup: string          // WWG | The Vault | (unattributed) — fin.entities.group_tag
  entityId: string             // fin.entities.id, where the warehouse resolved one
  jurisdiction: string         // CY | UAE | CH | KY | UK | US
  quarterLabel: string         // "2026-Q1" — sortable, unlike "Q1"
  isCrypto: string             // "Crypto" | "Fiat" — from fin.dim_currency

  /* ── Source-specific metadata, previously loaded but never exposed ───── */
  vendor: string               // expenses: who was paid
  classification: string       // expenses: cost classification
  division: string             // model: Business / Function / Technology
  functionName: string         // expenses: function group
  team: string                 // payroll: team
  position: string             // payroll: role
  agreementType: string        // payroll: employment vs contractor
  instrument: string           // positions: instrument category
  status: string               // jira: invoice status
}

/** Every field above that is not a measure, with a stable default. */
export const EMPTY_DIMS = {
  entityGroup: UNATTRIBUTED,
  entityId: '',
  jurisdiction: '(unknown)',
  quarterLabel: '',
  isCrypto: 'Fiat',
  vendor: '(none)',
  classification: '(none)',
  division: '(none)',
  functionName: '(none)',
  team: '(none)',
  position: '(none)',
  agreementType: '(none)',
  instrument: '(none)',
  status: '(none)',
}

export interface TreeGroup {
  id: string
  label: string
  children: { id: string; label: string }[]
}

export interface DimDef {
  id: string
  label: string
  values: string[]
}

export interface SplitGroup {
  id: string
  label: string
  children: DimDef[]
}

/**
 * FX lives in CurrencyContext now, backed by /api/fin/fx-rates.
 *
 * This module used to export a hardcoded { EUR: 1, USD: 1.08 }. It drifted
 * about 6% from the real rate, offered no currency beyond those two, and was
 * a second source of truth alongside the one the rest of the app already
 * used. Consumers should call useCurrency().convertBetween().
 */

/**
 * How well each dimension is populated for each data source, 0..1.
 *
 * Dimensions are NOT universal. `clientName` is real on revenue and wallet
 * rows but literally '(none)' on costs; `counterparty` is real on costs but
 * absent from wallet transactions. Splitting a cost measure by Client
 * therefore collapses every row into one '(none)' bucket — a chart that looks
 * fine and says nothing.
 *
 * This is measured from the loaded rows rather than hardcoded, so it stays
 * true as sources change. The workbench uses it to warn before a combination
 * silently produces a meaningless chart.
 */
export type DimCoverage = Record<string, Record<string, number>>

export interface LoadedData {
  DATA: DataRow[]
  /** dimension id → dataSource → fraction of rows carrying a real value */
  DIM_COVERAGE: DimCoverage
  VALUE_TREE: TreeGroup[]
  SPLIT_TREE: SplitGroup[]
  ALL_DIMS: DimDef[]
  DIM_FIELD: Record<string, string>
  FIELD_ORDER: Record<string, string[]>
  years: string[]
  entities: string[]
  /** Every group with rows, for the view toggle. Present on all views. */
  groups: { tag: string; label: string; rows: number }[]
  /** Rows held back from a group view because their payer belongs to no
      registry entity. Absent on the ALL view, which shows everything. */
  residual?: {
    /** Still unanswered — the working list. */
    rows: number
    value: number
    bySource: { source: string; rows: number; value: number }[]
    payers: string[]
    /** Answered, and the answer was "not ours". Excluded, not outstanding. */
    externalRows: number
    externalValue: number
  }
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('fin_auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchDataset<T>(source: string, entity?: string): Promise<T[]> {
  try {
    let url = `/api/fin/dataset?source=${encodeURIComponent(source)}`
    if (entity) url += `&entity=${encodeURIComponent(entity)}`
    const res = await fetch(url, { headers: authHeaders() })
    if (!res.ok) return []
    return await res.json()
  } catch {
    return []
  }
}

/* ═══════════════════════════════════════════════════════════
   CONFORMED DIMENSIONS
   Shared reference data, fetched once. These are what let a model
   forecast and a GL actual be grouped by the same taxonomy.
   ═══════════════════════════════════════════════════════════ */
interface EntityMeta { id: string; group: string; jurisdiction: string; name: string }
/** One period of a company's membership, from fin.entity_group_history. */
interface GroupWindow { group: string; from: string; to: string }
let _entityMeta: Map<string, EntityMeta> | null = null
let _entityById: Map<string, EntityMeta> | null = null
let _groupHistory: Map<string, GroupWindow[]> = new Map()
let _externalKeys: Set<string> = new Set()
let _cryptoCodes: Set<string> | null = null

const GROUP_LABEL: Record<string, string> = { wwg: 'WWG', vault: 'The Vault', corp: 'Corporate', tff: 'TFF' }

/**
 * The groups that actually have rows, biggest first — the toggle's buttons.
 *
 * Read from the data rather than declared. TFF became a peer of WWG and The
 * Vault after this toggle was built, and a hardcoded pair of buttons would
 * have dropped its rows out of every view while reporting nothing — the same
 * failure as slicing The Vault with `group_tag !== 'wwg'`. A group added to
 * `fin.entities` tomorrow gets its own button instead.
 *
 * Unattributed and external rows are not a group and get no button; the
 * residual reports them.
 */
function groupsPresent(rows: DataRow[]): { tag: string; label: string; rows: number }[] {
  const byLabel = new Map<string, string>()
  for (const [tag, label] of Object.entries(GROUP_LABEL)) byLabel.set(label, tag)
  const counts = new Map<string, number>()
  for (const r of rows) {
    if (r.entityGroup === UNATTRIBUTED || r.entityGroup === EXTERNAL) continue
    counts.set(r.entityGroup, (counts.get(r.entityGroup) || 0) + 1)
  }
  return [...counts.entries()]
    .map(([label, n]) => ({ tag: byLabel.get(label) || label, label, rows: n }))
    .sort((a, b) => b.rows - a.rows)
}

/**
 * Alias key — the same normalisation `db:resolve-entities` uses.
 *
 * Case and punctuation are dropped so "WISE WOLVES INTERNATIONAL LTD",
 * "Wise Wolves International Ltd" and "wise-wolves-international-ltd" are one
 * key. Matching on the raw string would leave the warehouse and the workbench
 * disagreeing about who paid for something.
 */
function aliasKey(s: string): string {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * A DATE column as 'YYYY-MM-DD', whatever shape it arrived in.
 *
 * The API hands back a string; a direct pg connection hands back a Date. The
 * naive String(v).slice(0, 10) turns the second into "Wed Jan 01", which
 * compares as less than every real date — so a dated group rule silently
 * never matched and every historic row kept the company's current group.
 * Formatted from local parts rather than toISOString(), because a calendar
 * day pushed through UTC can come back as the day before.
 */
function isoDate(v: unknown): string {
  if (!v) return ''
  if (typeof v === 'string') return v.slice(0, 10)
  const d = v instanceof Date ? v : new Date(v as any)
  if (isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * Display names for the `dataSource` codes.
 *
 * These codes are row keys and are referenced by saved templates and preset
 * filters, so they stay as-is in the data — but they are also shown to users
 * as filter checkboxes and chart series, where "budget_payroll" and
 * "jira_invoices" read as database internals. The workbench renders values
 * through these labels; nothing downstream compares against them.
 *
 * "(plan)" is part of the label on purpose: those three sources are the fin
 * model forecast, and they sit in the same measure as the actuals.
 */
export const SOURCE_LABEL: Record<string, string> = {
  costs: 'Costs',
  revenue: 'Revenue',
  net: 'Net P/L',
  positions: 'Client positions',
  transactions: 'Transactions',
  balances: 'Balances',
  fees: 'Fees',
  jira_invoices: 'Supplier invoices',
  payroll_actual: 'Payroll detail',
  budget_expense: 'Expenses (plan)',
  budget_payroll: 'Payroll (plan)',
  pl_plan: 'P&L (plan)',
}

/** Per-source name for that source's own "category" dimension. */
const SOURCE_CATEGORY_LABEL: Record<string, string> = {
  costs: 'Cost category',
  revenue: 'Revenue group',
  positions: 'Asset class',
  transactions: 'Transaction type',
  balances: 'Product',
  fees: 'Fee type',
  net: 'Net P/L',
  jira_invoices: 'Supplier',
  payroll_actual: 'Payroll line',
  budget_expense: 'Expense category (plan)',
  budget_payroll: 'Payroll category (plan)',
  pl_plan: 'P&L line (plan)',
}

async function loadReference(): Promise<void> {
  if (_entityMeta && _cryptoCodes) return
  const [ents, aliases, history, ccy] = await Promise.all([
    fetchDataset<any>('entities'),
    fetchDataset<any>('entity_aliases'),
    fetchDataset<any>('entity_group_history'),
    fetchDataset<any>('dim_currency'),
  ])
  // Keyed by id AND by display name, because fact rows carry entity as a
  // human label ("Wise Wolves Finance") rather than an id.
  const m = new Map<string, EntityMeta>()
  const byId = new Map<string, EntityMeta>()
  for (const e of ents) {
    const meta: EntityMeta = {
      id: String(e.id || ''),
      group: GROUP_LABEL[e.group_tag] || e.group_tag || UNATTRIBUTED,
      jurisdiction: e.jurisdiction || '(unknown)',
      name: e.name || e.id,
    }
    if (e.id) { byId.set(meta.id, meta); m.set(aliasKey(e.id), meta) }
    if (e.name) m.set(aliasKey(e.name), meta)
    if (e.legal_name) m.set(aliasKey(e.legal_name), meta)
  }
  // fin.entity_aliases is the warehouse's own spelling map — the same one
  // db:resolve-entities uses to populate entity_id, and the one maintained in
  // Records. Reading it here rather than keeping a second hand-written map in
  // the client is what stops the dashboard and the warehouse from disagreeing
  // about who paid for what.
  const external = new Set<string>()
  for (const a of aliases) {
    const key = a.alias_norm ? String(a.alias_norm).replace(/[^a-z0-9]/g, '') : aliasKey(a.alias)
    if (!key) continue
    const hit = byId.get(String(a.entity_id || ''))
    if (hit) { if (!m.has(key)) m.set(key, hit); continue }
    // No entity, and someone has said it is not one of ours. That is an answer,
    // not a gap: it stops the payer being counted as work still to do.
    if (a.is_entity === false) external.add(key)
  }
  _externalKeys = external

  // Effective-dated membership, newest window first so the first match wins.
  const moves = new Map<string, GroupWindow[]>()
  for (const h of history) {
    const id = String(h.entity_id || '')
    const from = isoDate(h.effective_from)
    if (!id || !from) continue
    const arr = moves.get(id) || []
    arr.push({
      group: GROUP_LABEL[h.group_tag] || String(h.group_tag),
      from,
      to: isoDate(h.effective_to) || '9999-12-31',
    })
    moves.set(id, arr)
  }
  for (const arr of moves.values()) arr.sort((a, b) => (a.from < b.from ? 1 : -1))
  _groupHistory = moves

  _entityMeta = m
  _entityById = byId
  _cryptoCodes = new Set(ccy.filter((c: any) => c.is_crypto).map((c: any) => String(c.code)))
}

const _resolveCache = new Map<string, EntityMeta | null>()

/**
 * The company a source's spelling refers to, or null.
 *
 * Exact match on the normalised alias first — that covers every id, name,
 * legal name and every spelling anyone has recorded in Records. The loose
 * contains-match after it is a net for labels the transforms invent, and it is
 * why the alias table matters: each spelling someone files there is one fewer
 * row relying on a fuzzy match to land in the right group.
 */
function resolveEntity(label: string): EntityMeta | null {
  const meta = _entityMeta
  if (!meta) return null
  const key = aliasKey(label)
  if (!key) return null
  if (_resolveCache.has(key)) return _resolveCache.get(key)!
  let hit = meta.get(key) ?? null
  if (!hit) {
    for (const [k, v] of meta) {
      if (k.length > 3 && (key.includes(k) || k.includes(key))) { hit = v; break }
    }
  }
  _resolveCache.set(key, hit)
  return hit
}

/**
 * Fill the conformed dimensions on every row, from the shared reference data.
 *
 * Resolution order matters, because `entityGroup` decides which group view a
 * row appears in:
 *
 *  1. `entity_id` — what the warehouse resolver already decided. Every GL,
 *     wallet, payroll and model row carries one except 898 finmodel_pl rows.
 *  2. the alias map, on the row's entity label.
 *  3. a loose contains-match, kept for labels the transforms invent
 *     ("Tria Wallet" is not a registry name).
 *
 * Then the group, which is not simply the company's current group: a company
 * that moved between groups has dated windows in `fin.entity_group_history`,
 * and a row belongs to whichever group owned the company on the row's own
 * date. Stamping today's answer onto a 2025 invoice moves money across the
 * WWG/Vault boundary, and that boundary does not wash out on consolidation.
 *
 * Anything still unmatched is either EXTERNAL — someone has said it is not
 * ours — or UNATTRIBUTED, meaning nobody has looked yet. Both stay out of the
 * group views; only the second is reported as outstanding.
 */
function applyConformedDims(rows: DataRow[]): DataRow[] {
  const meta = _entityMeta
  const byId = _entityById
  const crypto = _cryptoCodes
  if (!meta) return rows

  /** Group as at the row's own date, falling back to the current one. */
  const groupOn = (e: EntityMeta, r: DataRow): string => {
    const windows = _groupHistory.get(e.id)
    if (!windows?.length) return e.group
    // yearMonth is "2025-01"; a day of 01 keeps a month-end effective_to
    // inclusive of the whole month before it.
    const d = r.yearMonth ? `${r.yearMonth}-01` : r.year ? `${r.year}-01-01` : ''
    if (!d) return e.group
    for (const w of windows) if (d >= w.from && d <= w.to) return w.group
    return e.group
  }

  for (const r of rows) {
    const e = (r.entityId && byId?.get(r.entityId)) || resolveEntity(r.entity)
    if (e) {
      r.entityGroup = groupOn(e, r)
      r.entityId = e.id
      r.jurisdiction = e.jurisdiction
      // One company, one member of the Entity dimension. The sources spell
      // the same company several ways — "Wise Wolves Finance" and "Wise
      // Wolves Finance Ltd", "Tria Software" and "TRIA SOFTWARE LIMITED (ex.
      // WISE WOLVES DA LTD)", "Staya" and "Staya Ltd (CY)" — and left alone
      // they draw as separate series, which is precisely the mistake a
      // group-wide view is supposed to stop you making. The registry name
      // wins; unresolved rows keep whatever the source called them.
      if (e.name) r.entity = e.name
    } else if (_externalKeys.has(aliasKey(r.entity))) {
      r.entityGroup = EXTERNAL
    }
    r.quarterLabel = r.year && r.quarter ? `${r.year}-${r.quarter}` : ''
    if (crypto && r.currency) r.isCrypto = crypto.has(r.currency) ? 'Crypto' : 'Fiat'
  }
  return rows
}

function unique(arr: (string | null | undefined)[]): string[] {
  return [...new Set(arr.filter(Boolean) as string[])].sort()
}

/**
 * Synthesize monthly "net P/L" rows from existing cost + revenue rows.
 *
 * The chart engine supports only one chart type per panel, so there is no
 * native way to overlay a net-P/L line on a revenue-vs-expenses bar chart.
 * Instead we append precomputed net rows (dataSource='net', flowType='Cash
 * Flow') so any panel that splits dataSource on [revenue, costs, net] gets
 * three visible series — typically rendered as a line chart for cashflow
 * panels or as bars alongside revenue/costs.
 *
 * Appends one row per (entity, year, month) where cost or revenue data
 * exists. Safe to call once at the end of a loader's transform phase.
 */
function synthesizeNetPL(rows: DataRow[]): DataRow[] {
  const totals = new Map<string, { revenue: number; costs: number; entity: string; entityId: string; year: string; month: string; quarter: string; yearMonth: string }>()
  for (const r of rows) {
    if (r.dataSource !== 'revenue' && r.dataSource !== 'costs') continue
    const key = `${r.entity}|${r.year}|${r.month}`
    let agg = totals.get(key)
    if (!agg) {
      // entityId travels with the aggregate: a net row that lost it would fall
      // back to matching "Wise Wolves Finance" by name, and land in neither
      // group view if that match ever stopped working.
      agg = { revenue: 0, costs: 0, entity: r.entity, entityId: r.entityId, year: r.year, month: r.month, quarter: r.quarter, yearMonth: r.yearMonth }
      totals.set(key, agg)
    }
    if (r.dataSource === 'revenue') agg.revenue += r.value
    else agg.costs += r.value
  }
  const netRows: DataRow[] = []
  for (const agg of totals.values()) {
    netRows.push({
      ...EMPTY_DIMS,
      year: agg.year,
      quarter: agg.quarter,
      month: agg.month,
      yearMonth: agg.yearMonth,
      entity: agg.entity,
      entityId: agg.entityId,
      account: 'Net P/L',
      accountCode: '',
      scenario: 'Actual',
      dataSource: 'net',
      flowType: 'Cash Flow',
      costType: 'Net',
      counterparty: '(computed)',
      counterpartyType: 'Computed',
      clientName: '(none)',
      currency: 'EUR',
      entrySide: 'net',
      contraCategory: '(none)',
      serviceType: '',
      value: agg.revenue - agg.costs,
    })
  }
  return netRows
}

/* ═══════════════════════════════════════════════════════════
   WWF DATA LOADER — original brokerage data
   ═══════════════════════════════════════════════════════════ */
export async function loadWWFData(): Promise<LoadedData> {
  await loadReference()
  const [costs, revenue, positions] = await Promise.all([
    fetchDataset<any>('gl_costs', 'WWF'),
    fetchDataset<any>('gl_revenue', 'WWF'),
    fetchDataset<any>('gl_fin_positions', 'WWF'),
  ])

  const rows: DataRow[] = []

  for (const c of costs) {
    if (c.entry_side === 'credit') continue
    rows.push({
      ...EMPTY_DIMS,
      year: String(c.year_num),
      quarter: `Q${c.quarter_num}`,
      month: MONTHS[(c.month_num || 1) - 1],
      yearMonth: c.year_month || `${c.year_num}-${String(c.month_num).padStart(2, '0')}`,
      entity: c.entity_name || 'Wise Wolves Finance',
      entityId: c.entity_id || '',
      account: c.cost_category || c.account_name || 'Other',
      accountCode: c.account_code || '',
      scenario: 'Actual',
      dataSource: 'costs',
      flowType: 'Cash Flow',
      costType: c.cost_type || 'Other',
      counterparty: c.entity_name || '(unattributed)',
      counterpartyType: 'Vendor',
      clientName: '(none)',
      currency: 'EUR',
      entrySide: c.entry_side || 'debit',
      contraCategory: c.contra_category || '(none)',
      serviceType: '',
      value: Number(c.amount_eur) || 0,
    })
  }

  for (const r of revenue) {
    const mn = Number(r.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(r.year_num),
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: r.year_month || `${r.year_num}-${String(mn).padStart(2, '0')}`,
      entity: 'Wise Wolves Finance',
      entityId: r.entity_id || '',
      account: r.revenue_group || r.revenue_category || 'Other Revenue',
      accountCode: r.account_code || '',
      scenario: 'Actual',
      dataSource: 'revenue',
      flowType: 'Cash Flow',
      costType: 'Revenue',
      counterparty: r.client_name || '(unknown)',
      counterpartyType: r.counterparty_type || 'Client',
      clientName: r.client_name || '(unknown)',
      currency: 'EUR',
      entrySide: r.entry_side || 'credit',
      contraCategory: '(none)',
      serviceType: '',
      value: Number(r.amount) || 0,
    })
  }

  for (const p of positions) {
    const mn = Number(p.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(p.year_num),
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: p.year_month || `${p.year_num}-${String(mn).padStart(2, '0')}`,
      entity: 'Wise Wolves Finance',
      entityId: p.entity_id || '',
      account: p.asset_class || p.service_type || 'Other',
      accountCode: '',
      scenario: 'Actual',
      dataSource: 'positions',
      instrument: p.instrument_category || '(none)',
      flowType: 'Assets Held',
      costType: 'Position',
      counterparty: p.client_name || '(unknown)',
      counterpartyType: 'Client',
      clientName: p.client_name || '(unknown)',
      currency: p.currency || 'EUR',
      entrySide: 'balance',
      contraCategory: '(none)',
      serviceType: p.service_type || '',
      value: Number(p.net_cost_eur) || 0,
    })
  }

  // Merge plan rows for WWF from the fin model so the workbench can show
  // plan vs actual side-by-side.
  rows.push(...await loadFinModelData('wwf'))
  // Merge Jira A/P invoices for WWF.
  rows.push(...await loadJiraInvoices('wwf'))
  // Merge actual payroll paid (per employee per month) for WWF.
  rows.push(...await loadPayrollActual('wwf'))
  rows.push(...synthesizeNetPL(rows))
  return buildLoadedData(rows, ['costs', 'revenue', 'positions', 'budget_expense', 'budget_payroll', 'pl_plan', 'jira_invoices', 'payroll_actual', 'net'])
}

/* ═══════════════════════════════════════════════════════════
   WWDA DATA LOADER — Tria Wallet crypto platform
   ═══════════════════════════════════════════════════════════ */
export async function loadWWDAData(): Promise<LoadedData> {
  await loadReference()
  const [transactions, balances, fees] = await Promise.all([
    fetchDataset<any>('wwda_transactions', 'WWDA'),
    fetchDataset<any>('wwda_fact_balances', 'WWDA'),
    fetchDataset<any>('wwda_fees', 'WWDA'),
  ])

  const rows: DataRow[] = []

  // ── Transform transactions ──
  for (const tx of transactions) {
    const ym = tx.year_month || ''
    const mn = Number(tx.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(tx.year_num),
      quarter: `Q${tx.quarter_num || Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: ym,
      entity: tx.company_name || 'Tria Wallet',
      entityId: tx.entity_id || '',
      account: tx.operation_category || 'Other',
      accountCode: tx.transaction_type_code || '',
      scenario: 'Actual',
      dataSource: 'transactions',
      flowType: 'Transaction Volume',
      costType: tx.operation_category || 'Other',
      counterparty: tx.client_name || '(unknown)',
      counterpartyType: 'Client',
      clientName: tx.client_name || '(unknown)',
      currency: tx.currency_name || tx.currency_code || 'USDT',
      entrySide: tx.record_kind === 0 ? 'receipt' : 'expense',
      contraCategory: tx.product_name || '(none)',
      serviceType: '',
      // EUR, not the raw token amount: summing BTC and USDT together as
      // bare numbers made BTC (17% of real value) contribute ~0.0003% of
      // the total. amount_eur is priced at each row's own date.
      value: Number(tx.amount_eur) || 0,
    })
  }

  // ── Transform balances ──
  for (const b of balances) {
    const ym = b.year_month || ''
    const parts = ym.split('-')
    const yr = parts[0] || '2024'
    const mn = Number(parts[1]) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: yr,
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: ym,
      entity: 'Tria Wallet',
      entityId: b.entity_id || '',
      account: b.product_name || 'Current',
      accountCode: '',
      scenario: 'Actual',
      dataSource: 'balances',
      flowType: 'Balance',
      costType: 'Balance',
      counterparty: b.client_name || '(unknown)',
      counterpartyType: 'Client',
      clientName: b.client_name || '(unknown)',
      currency: b.currency_name || b.currency_code || 'USDT',
      entrySide: 'balance',
      contraCategory: '(none)',
      serviceType: '',
      value: Number(b.balance_amount) || 0,
    })
  }

  // ── Transform fees ──
  for (const f of fees) {
    const ym = f.year_month || ''
    const parts = ym.split('-')
    const yr = parts[0] || '2024'
    const mn = Number(parts[1]) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: yr,
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: ym,
      entity: f.company_name || 'Tria Wallet',
      entityId: f.entity_id || '',
      account: f.fee_type || 'Fee',
      accountCode: '',
      scenario: 'Actual',
      dataSource: 'fees',
      flowType: 'Fee Revenue',
      costType: 'Fee',
      counterparty: f.client_name || '(unknown)',
      counterpartyType: 'Client',
      clientName: f.client_name || '(unknown)',
      currency: f.fee_currency || 'USDT',
      entrySide: 'debit',
      contraCategory: f.product_name || '(none)',
      serviceType: '',
      value: Number(f.amount_eur) || 0,   // EUR, not the raw token amount
    })
  }

  return buildLoadedData(rows, ['transactions', 'balances', 'fees'])
}

/* ═══════════════════════════════════════════════════════════
   GL-style loader (shared between TB and WWPI)
   Both pipelines emit the same fact_costs / fact_revenue / fact_balances
   shape from a 1C GL register; this helper turns that JSON into DataRows
   tagged with the entity display name passed in.
   ═══════════════════════════════════════════════════════════ */
async function loadGLEntity(
  entityLabel: string,
  entityCode: string,
  entityId: string,
): Promise<LoadedData> {
  await loadReference()
  const [costs, revenue, balances] = await Promise.all([
    fetchDataset<any>('gl_costs', entityCode),
    fetchDataset<any>('gl_revenue', entityCode),
    fetchDataset<any>('gl_balances', entityCode),
  ])

  const rows: DataRow[] = []

  for (const c of costs) {
    if (c.entry_side === 'credit') continue
    const mn = Number(c.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(c.year_num),
      quarter: `Q${c.quarter_num || Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: c.year_month || `${c.year_num}-${String(mn).padStart(2, '0')}`,
      entity: c.entity_name || entityLabel,
      entityId: c.entity_id || '',
      account: c.cost_category || c.account_name || 'Other',
      accountCode: c.account_code || '',
      scenario: 'Actual',
      dataSource: 'costs',
      flowType: 'Cash Flow',
      costType: c.cost_type || 'Other',
      counterparty: c.contra_account_name || '(unattributed)',
      counterpartyType: 'Vendor',
      clientName: '(none)',
      currency: 'EUR',
      entrySide: c.entry_side || 'debit',
      contraCategory: c.contra_category || '(none)',
      serviceType: '',
      value: Number(c.amount_eur) || 0,
    })
  }

  for (const r of revenue) {
    const mn = Number(r.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(r.year_num),
      quarter: `Q${r.quarter_num || Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: r.year_month || `${r.year_num}-${String(mn).padStart(2, '0')}`,
      entity: entityLabel,
      entityId: r.entity_id || '',
      account: r.revenue_group || r.revenue_category || r.account_name || 'Other Revenue',
      accountCode: r.account_code || '',
      scenario: 'Actual',
      dataSource: 'revenue',
      flowType: 'Cash Flow',
      costType: 'Revenue',
      counterparty: r.client_name || r.account_name || '(operational)',
      counterpartyType: r.counterparty_type || 'Operational',
      clientName: r.client_name || '(none)',
      currency: 'EUR',
      entrySide: r.entry_side || 'credit',
      contraCategory: '(none)',
      serviceType: '',
      value: Number(r.amount) || 0,
    })
  }

  for (const b of balances) {
    const mn = Number(b.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(b.year_num),
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: b.year_month || `${b.year_num}-${String(mn).padStart(2, '0')}`,
      entity: entityLabel,
      entityId: b.entity_id || '',
      account: b.asset_class || b.account_name || 'Other',
      accountCode: b.account_code || '',
      scenario: 'Actual',
      dataSource: 'balances',
      flowType: 'Balance',
      costType: 'Balance',
      counterparty: b.account_name || '(account)',
      counterpartyType: 'Own Account',
      clientName: '(none)',
      currency: 'EUR',
      entrySide: 'balance',
      contraCategory: '(none)',
      serviceType: b.classification || '',
      value: Number(b.amount_eur) || 0,
    })
  }

  // Merge plan rows for the GL entity so the workbench can show plan vs
  // actual side-by-side.
  rows.push(...await loadFinModelData(entityId))
  // Merge Jira A/P invoices for the GL entity.
  rows.push(...await loadJiraInvoices(entityId))
  // Merge actual payroll paid for the GL entity.
  rows.push(...await loadPayrollActual(entityId))
  rows.push(...synthesizeNetPL(rows))
  return buildLoadedData(rows, ['costs', 'revenue', 'balances', 'budget_expense', 'budget_payroll', 'pl_plan', 'jira_invoices', 'payroll_actual', 'net'])
}

/* ═══════════════════════════════════════════════════════════
   TB DATA LOADER — Tria Bridge (CY CASP / MiCA crypto custody)
   ═══════════════════════════════════════════════════════════ */
export async function loadTBData(): Promise<LoadedData> {
  return loadGLEntity('Tria Bridge', 'TB', 'tb')
}

/* ═══════════════════════════════════════════════════════════
   WWPI DATA LOADER — Wise Wolves Payment Institution
   ═══════════════════════════════════════════════════════════ */
export async function loadWWPIData(): Promise<LoadedData> {
  return loadGLEntity('Wise Wolves Payment Institution', 'WWPI', 'wwpi')
}

/* ═══════════════════════════════════════════════════════════
   FIN MODEL LOADER — Plan data from the WW Corp Excel model
   Three sources: Expenses_List, Contracts_Payroll, PL Master + PL Legal
   Entities. All rows are tagged with scenario='Plan'. The same DataRow
   shape lets the workbench show plan vs actual side-by-side.
   ═══════════════════════════════════════════════════════════ */
export async function loadFinModelData(entityId?: string): Promise<DataRow[]> {
  // Returns nothing rather than being unwired from its four call sites: the
  // forecast is meant to come back, and this is the whole of what keeps it
  // out. It also means the three model queries are not run at all.
  if (!INCLUDE_PLAN) return []
  await loadReference()
  const [expenses, payroll, pl] = await Promise.all([
    fetchDataset<any>('finmodel_expenses'),
    fetchDataset<any>('finmodel_payroll'),
    fetchDataset<any>('finmodel_pl'),
  ])

  const rows: DataRow[] = []

  // ── Non-payroll expenses (Bloomberg, audit, software, regulatory, …) ──
  for (const e of expenses) {
    const eid = String(e.entity_id || '') || resolveEntity(e.entity)?.id || ''
    if (entityId && eid !== entityId) continue
    const mn = Number(e.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(e.year_num),
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: e.year_month || `${e.year_num}-${String(mn).padStart(2, '0')}`,
      entity: e.entity,
      entityId: eid,
      account: e.classification || 'Other',
      accountCode: e.expense_id || '',
      scenario: 'Plan',
      dataSource: 'budget_expense',
      vendor: e.vendor || '(none)',
      classification: e.classification || '(none)',
      division: e.division || '(none)',
      functionName: e.function_name || '(none)',
      flowType: 'Cash Flow',
      costType: e.opex_capex || 'OPEX',
      counterparty: e.vendor || e.expense_line || '(unattributed)',
      counterpartyType: 'Vendor',
      clientName: '(none)',
      currency: 'EUR',
      entrySide: 'debit',
      contraCategory: e.category || '(none)',
      serviceType: e.function || '',
      value: Number(e.amount_eur) || 0,
    })
  }

  // ── Payroll allocations from Contracts_Payroll ──
  for (const p of payroll) {
    const eid = String(p.entity_id || '') || resolveEntity(p.entity)?.id || ''
    if (entityId && eid !== entityId) continue
    const mn = Number(p.month_num) || 1
    rows.push({
      ...EMPTY_DIMS,
      year: String(p.year_num),
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: p.year_month || `${p.year_num}-${String(mn).padStart(2, '0')}`,
      entity: p.entity,
      entityId: eid,
      account: 'Payroll & Staff',
      accountCode: p.contract_id || '',
      scenario: 'Plan',
      dataSource: 'budget_payroll',
      // Trimmed: the source spreadsheet carries trailing spaces on several
      // titles ("Executive Director / Chief Operating Officer "), which split
      // one position into two dimension members that look identical.
      division: p.division?.trim() || '(none)',
      team: p.team?.trim() || '(none)',
      position: p.position?.trim() || '(none)',
      agreementType: p.agreement_type?.trim() || '(none)',
      flowType: 'Cash Flow',
      costType: p.opex_capex || 'OPEX',
      counterparty: p.person || '(unattributed)',
      counterpartyType: 'Staff',
      clientName: '(none)',
      currency: p.currency || 'EUR',
      entrySide: 'debit',
      contraCategory: p.position || '(none)',
      serviceType: p.team || '',
      value: Number(p.amount_eur) || 0,
    })
  }

  // ── PL plan rows (revenue + opex by metric) ──
  for (const r of pl) {
    const eid = String(r.entity_id || '') || resolveEntity(r.entity)?.id || ''
    if (entityId && eid !== entityId) continue
    const mn = Number(r.month_num) || 1
    // Filter to revenue and opex; skip Balances/Capital/Net to avoid double
    // counting (those are derived from the other lines).
    const mt = String(r.metric_type || '')
    const isRevenue = mt === 'Revenue'
    const isOpex = mt === 'Opex'
    if (!isRevenue && !isOpex) continue
    const amt = Number(r.amount_eur) || 0
    if (amt === 0) continue
    rows.push({
      ...EMPTY_DIMS,
      year: String(r.year_num),
      quarter: `Q${Math.ceil(mn / 3)}`,
      month: MONTHS[mn - 1],
      yearMonth: r.year_month || `${r.year_num}-${String(mn).padStart(2, '0')}`,
      entity: r.entity,
      entityId: eid,
      account: r.metric || mt,
      accountCode: '',
      scenario: 'Plan',
      dataSource: 'pl_plan',
      flowType: 'Cash Flow',
      costType: isRevenue ? 'Revenue' : 'Opex',
      counterparty: '(plan)',
      counterpartyType: isRevenue ? 'Operational' : 'Vendor',
      clientName: '(none)',
      currency: 'EUR',
      entrySide: isRevenue ? 'credit' : 'debit',
      contraCategory: '(none)',
      serviceType: '',
      // PL opex amounts are stored as negatives in the model; flip to positive
      // so they sum cleanly alongside the actual cost rows.
      value: isOpex ? Math.abs(amt) : amt,
    })
  }

  return rows
}

/* ═══════════════════════════════════════════════════════════
   JIRA INVOICES LOADER — A/P invoices from the WW Jira backup
   The Jira instance was repurposed as an accounts-payable tracker;
   project INV holds 6,341 invoices with full custom-field metadata
   (issuer, recipient, amount, currency, dates, status, multi-line
   direct amount splits, payment way, classification).
   ═══════════════════════════════════════════════════════════ */
// The map that used to live here — twelve Jira payer spellings translated to
// display names — now lives in `fin.entity_aliases`, seeded by
// `npm run db:entity-groups`. It was a second mapping table in a file nobody
// with the answers could edit, and it disagreed with the warehouse: the
// warehouse had never been told that "Wise Wolves Fintech Ltd" is Tria FinTech,
// so 2,216 invoices resolved one way in the dashboard and not at all in SQL.

export async function loadJiraInvoices(entityId?: string): Promise<DataRow[]> {
  await loadReference()
  const invoices = await fetchDataset<any>('jira_invoices')
  const rows: DataRow[] = []

  for (const inv of invoices) {
    // Pick the date that best represents when the invoice impacts cash:
    // payment date if known, else invoice date, else created date.
    const dateStr = inv.date_of_payment || inv.invoice_date || inv.created || ''
    const datePart = String(dateStr).split('T')[0].split(' ')[0]
    const [yyyy, mm] = datePart.split('-')
    if (!yyyy || !mm) continue
    const yearNum = Number(yyyy)
    const monthNum = Number(mm)
    if (!yearNum || !monthNum) continue

    const rawEntity = inv.invoice_issued_to || '(Unattributed)'
    const payer = resolveEntity(rawEntity)
    if (entityId && payer?.id !== entityId) continue
    const entity = payer?.name || rawEntity

    const amount = Number(inv.amount) || 0
    if (amount === 0) continue

    // Classification → costType. Jira's "Direct or own expense" multi-checkbox
    // maps to one of: "Direct expense", "Own expense", or both. Default to
    // Direct since most are.
    const cls = String(inv.classification || '').toLowerCase()
    const costType = cls.includes('own') ? 'Own expense' : 'Direct'

    // Status → entrySide / scenario. Cancelled invoices don't represent
    // actual cash and should be excluded from totals.
    const status = String(inv.status || '')
    if (status === 'Cancel') continue

    // The status field also tells us where the invoice sits in the AP
    // workflow. We surface this as the contraCategory dim so the workbench
    // can pivot by approval stage.
    const wf = status === 'Paid' ? 'Paid'
            : status === 'Payment' || status === 'Payment by WWPI' ? 'Approved for payment'
            : status === 'New' ? 'New (unprocessed)'
            : 'In approval'

    rows.push({
      ...EMPTY_DIMS,
      year: String(yearNum),
      quarter: `Q${Math.ceil(monthNum / 3)}`,
      month: MONTHS[monthNum - 1],
      yearMonth: `${yearNum}-${String(monthNum).padStart(2, '0')}`,
      entity,
      // jira_invoices is the one fact table with no entity_id column, so the
      // payer string resolved through the alias table is the only identity
      // these rows have. Without it the covered-entity filter cannot tell
      // WWF's invoices from anyone else's and would add them twice.
      entityId: payer?.id || '',
      // Use the supplier's name as the account label so the "by category"
      // pivots actually mean something. Suppliers are the natural grouping
      // for AP analysis.
      account: inv.invoice_issued_from || '(Unknown supplier)',
      accountCode: inv.issue_key || '',
      scenario: 'Actual',
      dataSource: 'jira_invoices',
      status: inv.status || '(none)',
      classification: inv.classification || '(none)',
      flowType: 'Cash Flow',
      costType,
      counterparty: inv.invoice_issued_from || '(Unknown supplier)',
      counterpartyType: 'Vendor',
      clientName: inv.client_name || '(none)',
      currency: inv.invoice_currency || 'EUR',
      entrySide: 'debit',
      contraCategory: wf,
      serviceType: '',
      value: amount,
    })
  }

  return rows
}

/* ═══════════════════════════════════════════════════════════
   PAYROLL ACTUAL LOADER — historical paid payroll from the
   restored Cyprus payroll SQL Server DB. Per (entity × employee
   × month). Sits alongside fin model "Plan" payroll and 1C
   "Booked" 800300 PAYROLL EXPENSES.
   ═══════════════════════════════════════════════════════════ */
export async function loadPayrollActual(entityId?: string): Promise<DataRow[]> {
  await loadReference()
  const data = await fetchDataset<any>('payroll_actual')
  const rows: DataRow[] = []
  for (const p of data) {
    const entity = p.company_name
    const eid = String(p.entity_id || '') || resolveEntity(entity)?.id || ''
    if (entityId && eid !== entityId) continue
    const yearNum = Number(p.year_num)
    const monthNum = Number(p.month_num)
    if (!yearNum || !monthNum) continue
    const gross = Number(p.gross_eur) || 0
    if (gross === 0) continue
    // Use total cost (gross + employer SIC + NHS + COH + RED + IND) so the
    // workbench matches what 1C books to PAYROLL EXPENSES. Older extracts
    // without that field fall back to gross alone.
    const totalCost = Number(p.total_cost_eur ?? gross) || gross
    rows.push({
      ...EMPTY_DIMS,
      year: String(yearNum),
      quarter: `Q${Math.ceil(monthNum / 3)}`,
      month: MONTHS[monthNum - 1],
      yearMonth: p.year_month || `${yearNum}-${String(monthNum).padStart(2, '0')}`,
      entity,
      entityId: eid,
      // The "account" is fixed — this whole data source IS payroll. Putting
      // a single label here means the workbench's "by category" pivots
      // group all payroll cleanly into one bucket regardless of employee.
      account: 'Payroll & Staff Costs',
      accountCode: p.employee_code || '',
      scenario: 'Actual',
      dataSource: 'payroll_actual',
      flowType: 'Cash Flow',
      costType: 'Payroll',
      counterparty: p.employee_name || '(unknown)',
      counterpartyType: 'Staff',
      clientName: '(none)',
      currency: 'EUR',
      entrySide: 'debit',
      contraCategory: '(none)',
      serviceType: '',
      value: totalCost,
    })
  }
  return rows
}

/* ═══════════════════════════════════════════════════════════
   SHARED: Build LoadedData from rows
   ═══════════════════════════════════════════════════════════ */
function buildLoadedData(rows: DataRow[], allSources: string[]): LoadedData {
  applyConformedDims(rows)

  // Measured, not declared. The caller names every source the workbench knows
  // about; this keeps the ones that actually returned rows. A source listed
  // here with nothing behind it becomes a checkbox in the Data source filter
  // that selects no rows, and — if it reaches an x-axis — a row of empty bars
  // that reads as "zero" rather than "not loaded".
  const loaded = new Set(rows.map(r => r.dataSource))
  const dataSources = allSources.filter(s => loaded.has(s))

  const years = unique(rows.map(r => r.year))
  const entities = unique(rows.map(r => r.entity))
  const currencies = unique(rows.map(r => r.currency))
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4']

  const allCategories = unique(rows.map(r => r.account))
  const costTypes = unique(rows.map(r => r.costType))
  const counterpartyTypes = unique(rows.map(r => r.counterpartyType))
  const clients = unique(rows.filter(r => r.clientName !== '(none)' && r.clientName !== '(unknown)').map(r => r.clientName))
  const counterparties = unique(rows.map(r => r.counterparty))
  const contraCategories = unique(rows.filter(r => r.contraCategory !== '(none)').map(r => r.contraCategory))
  const serviceTypes = unique(rows.filter(r => r.serviceType !== '').map(r => r.serviceType))
  // Scenario dim — Actual (1C) vs Plan (fin model). Only surface as a real
  // dimension when both scenarios are present in the loaded data.
  const scenarios = unique(rows.map(r => r.scenario))

  // Conformed + newly surfaced dimensions. Each is only offered when the
  // loaded data actually carries more than one value — a picker entry with a
  // single option is noise.
  const notNone = (v: string) => v && v !== '(none)' && v !== '(unknown)'
  const entityGroups  = unique(rows.map(r => r.entityGroup).filter(notNone))
  const jurisdictions = unique(rows.map(r => r.jurisdiction).filter(notNone))
  const quarterLabels = unique(rows.map(r => r.quarterLabel).filter(Boolean))
  const cryptoFlags   = unique(rows.map(r => r.isCrypto).filter(Boolean))
  const vendors       = unique(rows.map(r => r.vendor).filter(notNone))
  const classifications = unique(rows.map(r => r.classification).filter(notNone))
  const divisions     = unique(rows.map(r => r.division).filter(notNone))
  const functions     = unique(rows.map(r => r.functionName).filter(notNone))
  const teams         = unique(rows.map(r => r.team).filter(notNone))
  const positionsDim  = unique(rows.map(r => r.position).filter(notNone))
  const agreements    = unique(rows.map(r => r.agreementType).filter(notNone))
  const instruments   = unique(rows.map(r => r.instrument).filter(notNone))
  const statuses      = unique(rows.map(r => r.status).filter(notNone))
  const dimIf = (vals: string[], id: string, label: string) =>
    vals.length > 1 ? [{ id, label, values: vals }] : []

  // Per-dataSource categories
  const categoryBySource: DimDef[] = []
  for (const ds of dataSources) {
    const vals = unique(rows.filter(r => r.dataSource === ds).map(r => r.account))
    if (vals.length > 0) {
      // Every source needs a human label. Falling back to the raw source key
      // put "jira_invoices" and "payroll_actual" in front of users as
      // dimension names.
      const label = SOURCE_CATEGORY_LABEL[ds] || `${SOURCE_LABEL[ds] || ds} category`
      categoryBySource.push({ id: `d_${ds}Category`, label, values: vals })
    }
  }

  /* ── Dimension coverage ─────────────────────────────────────────────────
     A value counts as "real" when it is not one of the placeholders the
     transforms use for absent data. Computed per (dimension, dataSource) so
     the workbench can tell a genuine zero from a dimension that simply does
     not apply to that measure. */
  const PLACEHOLDERS = new Set(['', '(none)', '(unknown)', '(unattributed)', '(n/a)', '—'])
  const DIM_FIELDS_FOR_COVERAGE: Record<string, keyof DataRow> = {
    d_account: 'account',
    d_costType: 'costType',
    d_counterparty: 'counterparty',
    d_counterpartyType: 'counterpartyType',
    d_clientName: 'clientName',
    d_currency: 'currency',
    d_contraCategory: 'contraCategory',
    d_serviceType: 'serviceType',
    d_entity: 'entity',
    d_scenario: 'scenario',
    // Conformed dimensions
    d_entityGroup: 'entityGroup',
    d_jurisdiction: 'jurisdiction',
    d_quarterLabel: 'quarterLabel',
    d_isCrypto: 'isCrypto',
    // Source metadata
    d_vendor: 'vendor',
    d_classification: 'classification',
    d_division: 'division',
    d_functionName: 'functionName',
    d_team: 'team',
    d_position: 'position',
    d_agreementType: 'agreementType',
    d_instrument: 'instrument',
    d_status: 'status',
  }
  const DIM_COVERAGE: DimCoverage = {}
  const rowsBySource = new Map<string, DataRow[]>()
  for (const r of rows) {
    const arr = rowsBySource.get(r.dataSource)
    if (arr) arr.push(r)
    else rowsBySource.set(r.dataSource, [r])
  }
  for (const [dimId, field] of Object.entries(DIM_FIELDS_FOR_COVERAGE)) {
    DIM_COVERAGE[dimId] = {}
    for (const [ds, dsRows] of rowsBySource) {
      let real = 0
      for (const r of dsRows) {
        const v = r[field]
        if (v !== null && v !== undefined && !PLACEHOLDERS.has(String(v))) real += 1
      }
      DIM_COVERAGE[dimId][ds] = dsRows.length ? real / dsRows.length : 0
    }
  }

  // Detect flow types present in data
  const flowTypes = unique(rows.map(r => r.flowType))
  const VALUE_TREE: TreeGroup[] = flowTypes.map(ft => ({
    id: `grp_${ft.replace(/\s+/g, '').toLowerCase()}`,
    label: ft,
    children: [{ id: `v_${ft.replace(/\s+/g, '').toLowerCase()}`, label: ft }],
  }))

  const SPLIT_TREE: SplitGroup[] = [
    {
      id: 'sg_time',
      label: 'Timeline',
      children: [
        { id: 'd_year', label: 'Year', values: years },
        { id: 'd_quarter', label: 'Quarter', values: quarters },
        ...dimIf(quarterLabels, 'd_quarterLabel', 'Quarter (dated)'),
        { id: 'd_month', label: 'Month', values: MONTHS },
      ],
    },
    {
      id: 'sg_class',
      label: 'Classification',
      children: [
        { id: 'd_dataSource', label: 'Data source', values: dataSources },
        { id: 'd_costType', label: 'Type', values: costTypes },
        { id: 'd_counterpartyType', label: 'Counterparty type', values: counterpartyTypes },
        ...(scenarios.length > 1 ? [{ id: 'd_scenario', label: 'Scenario', values: scenarios }] : []),
        ...(serviceTypes.length > 0 ? [{ id: 'd_serviceType', label: 'Service type', values: serviceTypes }] : []),
      ],
    },
    {
      id: 'sg_categories',
      label: 'Categories',
      children: [
        { id: 'd_account', label: 'Category', values: allCategories },
        ...categoryBySource,
      ],
    },
    ...(divisions.length > 1 || teams.length > 1 || vendors.length > 1 ? [{
      id: 'sg_org',
      label: 'Organisation',
      children: [
        ...dimIf(divisions, 'd_division', 'Division'),
        ...dimIf(teams, 'd_team', 'Team'),
        ...dimIf(positionsDim, 'd_position', 'Position'),
        ...dimIf(agreements, 'd_agreementType', 'Agreement type'),
        ...dimIf(functions, 'd_functionName', 'Function'),
        ...dimIf(vendors, 'd_vendor', 'Vendor'),
        ...dimIf(classifications, 'd_classification', 'Cost classification'),
        ...dimIf(instruments, 'd_instrument', 'Instrument'),
        ...dimIf(statuses, 'd_status', 'Status'),
      ],
    }] : []),
    {
      id: 'sg_counterparty',
      label: 'Counterparty',
      children: [
        { id: 'd_counterparty', label: 'Counterparty', values: counterparties },
        { id: 'd_clientName', label: 'Client', values: clients },
      ],
    },
    {
      id: 'sg_other',
      label: 'Other',
      children: [
        { id: 'd_entity', label: 'Entity', values: entities },
        ...dimIf(entityGroups, 'd_entityGroup', 'Group'),
        ...dimIf(jurisdictions, 'd_jurisdiction', 'Jurisdiction'),
        { id: 'd_currency', label: 'Currency', values: currencies },
        ...dimIf(cryptoFlags, 'd_isCrypto', 'Asset class'),
        ...(contraCategories.length > 0 ? [{ id: 'd_contraCategory', label: 'Product / Contra', values: contraCategories }] : []),
      ],
    },
  ].filter(g => g.children.length > 0)

  const ALL_DIMS = SPLIT_TREE.flatMap(g => g.children)

  // Build DIM_FIELD map — includes dynamic per-source categories
  const DIM_FIELD: Record<string, string> = {
    d_year: 'year',
    d_quarter: 'quarter',
    d_month: 'month',
    d_entity: 'entity',
    d_costType: 'costType',
    d_dataSource: 'dataSource',
    d_counterpartyType: 'counterpartyType',
    d_counterparty: 'counterparty',
    d_clientName: 'clientName',
    d_account: 'account',
    d_currency: 'currency',
    d_contraCategory: 'contraCategory',
    d_serviceType: 'serviceType',
    d_scenario: 'scenario',
    // Conformed — resolved through fin.entities / fin.dim_currency, so facts
    // from different sources group against the same taxonomy.
    d_entityGroup: 'entityGroup',
    d_jurisdiction: 'jurisdiction',
    d_quarterLabel: 'quarterLabel',
    d_isCrypto: 'isCrypto',
    // Source metadata that was loaded but never reachable from the picker.
    d_vendor: 'vendor',
    d_classification: 'classification',
    d_division: 'division',
    d_functionName: 'functionName',
    d_team: 'team',
    d_position: 'position',
    d_agreementType: 'agreementType',
    d_instrument: 'instrument',
    d_status: 'status',
  }
  // Map per-source category dims to account field
  for (const ds of dataSources) {
    DIM_FIELD[`d_${ds}Category`] = 'account'
  }

  const FIELD_ORDER: Record<string, string[]> = {
    month: [...MONTHS],
    quarter: quarters,
    year: years,
    entity: entities,
    costType: costTypes,
    dataSource: dataSources,
    counterpartyType: counterpartyTypes,
    counterparty: counterparties,
    clientName: clients,
    account: allCategories,
    currency: currencies,
    contraCategory: contraCategories,
    serviceType: serviceTypes,
    // Plan first so the visual order is Plan | Actual when both are stacked.
    scenario: ['Plan', 'Actual'].filter(s => scenarios.includes(s)),
  }

  // The groups in THESE rows — which for a filtered view is the one being
  // shown. loadAllData overrides it with the groups in the whole row set,
  // since the toggle has to offer the views you are not currently looking at.
  return { DATA: rows, DIM_COVERAGE, VALUE_TREE, SPLIT_TREE, ALL_DIMS, DIM_FIELD, FIELD_ORDER, years, entities,
           groups: groupsPresent(rows) }
}

const ALL_SOURCES = ['costs', 'revenue', 'positions', 'transactions', 'balances', 'fees',
                    'budget_expense', 'budget_payroll', 'pl_plan', 'jira_invoices',
                    'payroll_actual', 'net']

/**
 * Every fact row in the warehouse, from every source, loaded once.
 *
 * The four source loaders each cover the entities their system actually
 * carries — the 1C GL holds only WWF, WWPI and Tria Bridge; the wallet feeds
 * only Tria Software — so between them they are the complete set of actuals.
 * Plan, supplier invoices and payroll span the whole registry, so the rows
 * those loaders return for entities with no 1C presence are added on top.
 */
async function loadAllRows(): Promise<DataRow[]> {
  const [wwf, wwda, tb, wwpi, allPlanRows, allJiraRows, allPayrollRows] = await Promise.all([
    loadWWFData(),
    loadWWDAData(),
    loadTBData(),
    loadWWPIData(),
    loadFinModelData(),
    loadJiraInvoices(),
    loadPayrollActual(),
  ])
  // By id, not by display label. These three loaders already merged their own
  // company's plan, invoice and payroll rows above; everything else is added
  // here. Matching on the label meant that renaming a company to its registry
  // name — which conforming now does — would let its rows through twice.
  // The wallet loader merges none of these, so tria_sw is not covered.
  const coveredEntities = new Set(['wwf', 'wwpi', 'tb'])
  const uncovered = (r: DataRow) => !r.entityId || !coveredEntities.has(r.entityId)
  const extraPlan    = allPlanRows.filter(uncovered)
  const extraJira    = allJiraRows.filter(uncovered)
  const extraPayroll = allPayrollRows.filter(uncovered)
  const rows = [...wwf.DATA, ...wwda.DATA, ...tb.DATA, ...wwpi.DATA,
                ...extraPlan, ...extraJira, ...extraPayroll]
  // Conform here, not only in buildLoadedData. The group filter reads
  // entityGroup, and the four entity loaders are the only ones that have run
  // it — the plan, invoice and payroll rows added just above would still be
  // carrying the default. Filtering before this ran cost the WWG view 4,327
  // rows and reported them as unattributable.
  return applyConformedDims(rows)
}

/**
 * Cached because the three views are three slices of one row set.
 *
 * Switching WWG → The Vault used to mean a different set of fetches; now it
 * is a filter over rows already in memory, so the toggle does not go back to
 * the network at all.
 */
let _allRows: Promise<DataRow[]> | null = null

/** Drop the cache — for a hard refresh after data changes underneath us. */
export function invalidateData(): void { _allRows = null }

/* ═══════════════════════════════════════════════════════════
   ENTRY POINT — loads one group-wide view
   ═══════════════════════════════════════════════════════════ */
export async function loadAllData(view: GroupView = "ALL"): Promise<LoadedData> {
  if (!_allRows) _allRows = loadAllRows().catch(err => { _allRows = null; throw err })
  const all = await _allRows

  const groups = groupsPresent(all)
  // A view nobody has rows for — a stored tag from a build that had different
  // groups, or one whose data has since gone (TFF is only in the workbench
  // while the forecast is) — shows everything rather than an empty screen
  // with no explanation of what happened.
  const known = groups.some(g => g.tag === view)
  if (view === "ALL" || !known) return { ...buildLoadedData(all, ALL_SOURCES), groups }

  const wanted = GROUP_LABEL[view] || view
  const rows = all.filter(r => r.entityGroup === wanted)

  // Rows in neither group are reported rather than dropped in silence, which
  // is why WWG and The Vault do not have to add up to ALL. Only the ones
  // nobody has ruled on are counted as outstanding — a payer already marked
  // external in Records is a decision, and a badge that keeps nagging about
  // settled work is a badge people learn to ignore.
  const residualRows = all.filter(r => r.entityGroup === UNATTRIBUTED)
  const externalRows = all.filter(r => r.entityGroup === EXTERNAL)
  const bySource = new Map<string, { rows: number; value: number }>()
  for (const r of residualRows) {
    const agg = bySource.get(r.dataSource) || { rows: 0, value: 0 }
    agg.rows += 1
    agg.value += Math.abs(r.value)
    bySource.set(r.dataSource, agg)
  }

  return {
    ...buildLoadedData(rows, ALL_SOURCES),
    groups,
    residual: {
      rows: residualRows.length,
      value: residualRows.reduce((s, r) => s + Math.abs(r.value), 0),
      bySource: [...bySource.entries()]
        .map(([source, agg]) => ({ source, ...agg }))
        .sort((a, b) => b.value - a.value),
      payers: [...new Set(residualRows.map(r => r.entity))].sort(),
      externalRows: externalRows.length,
      externalValue: externalRows.reduce((s, r) => s + Math.abs(r.value), 0),
    },
  }
}
